// apps/client/src/pages/accounts/Accounts.tsx
import {
  ActivityAllocation,
  ProjectAccount,
  ResearchActivity,
  ResearchActivityType,
  UserRole,
  XeroSyncAccountsResponse,
} from '@avanzu/types';
import {
  AutoFixHigh,
  AutoFixHighOutlined,
  CheckCircle,
  DeleteSweep,
  PlaylistAddCheck,
  PlaylistRemove,
  ScienceOutlined,
  Search,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import HelpComponent from '../../components/HelpComponent';
import { LoadingComponent } from '../../components/LoadingComponent';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useDebounce } from '../../hooks/useDebounce';
import ContentWrapper from '../../layout/ContentWrapper';
import { deleteAccountAllocations, saveAccountAllocations } from '../../services/accounts';
import { fetchProjectAccountRules, fetchProjectActivities } from '../../services/projects';
import { convertAllocationsToObject } from '../../utils/convertAllocationsToObject';
import AccountAllocationRow from './components/AccountAllocationRow';
import XeroSyncAccounts from './components/XeroSyncAccounts';

export const AccountsPageRoles = [UserRole.AVANZU, UserRole.OWNER, UserRole.ADMIN, UserRole.MEMBER];

// Static sort functions
const sortAccountsByCode = (a: ProjectAccount, b: ProjectAccount) =>
  a.code.localeCompare(b.code, undefined, { numeric: true });

export const Accounts: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<ProjectAccount[]>([]);
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [syncResult, setSyncResult] = useState<XeroSyncAccountsResponse | null>(null);
  const [allocationsMap, setAllocationsMap] = useState<Record<string, Record<string, number>>>({});

  const { organisationId } = useAuth();
  const { currentProject } = useProject();
  const { enqueueSnackbar } = useSnackbar();
  const projectId = currentProject?.id;

  useEffect(() => {
    const codeParam = searchParams.get('code');
    if (codeParam) {
      setSearchFilter(codeParam);
      searchParams.delete('code');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedSearchFilter = useDebounce(searchFilter, 300);

  const loadData = useCallback(async (orgId: string, projId: string) => {
    if (!orgId || !projId) return;

    setLoading(true);
    setError(null);

    try {
      const [accountsResponse, activitiesResponse] = await Promise.all([
        // Backend applies org.allowedAccountTypes (default: [EXPENSE]),
        // so we don't need a client-side type/class filter here.
        fetchProjectAccountRules(orgId, projId, {
          allocatedOnly: false,
        }),
        fetchProjectActivities(orgId, projId),
      ]);

      if (accountsResponse.success && activitiesResponse.success) {
        const newAllocationsMap: Record<string, Record<string, number>> = {};
        accountsResponse.accounts?.forEach((account) => {
          if (account.allocations && account.allocations.length > 0) {
            newAllocationsMap[account.id] = convertAllocationsToObject(account.allocations);
          }
        });

        setAccounts(accountsResponse.accounts || []);
        setActivities(activitiesResponse.activities || []);
        setAllocationsMap(newAllocationsMap);
      } else {
        throw new Error(
          accountsResponse.message || activitiesResponse.message || 'Failed to load data'
        );
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (organisationId && currentProject?.id) {
      loadData(organisationId, currentProject.id);
    }
  }, [organisationId, currentProject?.id, loadData]);

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
  }, [activities]);

  const filteredAccounts = useMemo(() => {
    let filtered = accounts;
    if (debouncedSearchFilter) {
      const searchLower = debouncedSearchFilter.toLowerCase();
      filtered = accounts.filter(
        (account) =>
          account.code.toLowerCase().includes(searchLower) ||
          account.name.toLowerCase().includes(searchLower)
      );
    }
    return filtered.sort(sortAccountsByCode);
  }, [accounts, debouncedSearchFilter]);

  const handleSyncComplete = (result: XeroSyncAccountsResponse) => {
    setSyncResult(result);
    if (result.success) {
      enqueueSnackbar(`Successfully synced ${result.processed} accounts`, {
        variant: 'success',
      });
      loadData(organisationId!, projectId!);
    }
  };

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilter(event.target.value);
  }, []);

  const clearFilter = useCallback(() => {
    setSearchFilter('');
    searchParams.delete('code');
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  // NOTE: allocation editing now happens inside each AccountRow, so parent does not get updated on each keystroke.
  // Parent state (allocationsMap) is only updated when a row is saved / excluded / deleted.

  const handleCreateRule = useCallback(
    async (accountId: string, allocations: ActivityAllocation[]) => {
      if (!organisationId || !projectId) return;

      setSaving(accountId);
      try {
        await saveAccountAllocations(organisationId, projectId, accountId, allocations);

        setAllocationsMap((prev) => ({
          ...prev,
          [accountId]: convertAllocationsToObject(allocations),
        }));

        setAccounts((prev) =>
          prev.map((account) =>
            account.id === accountId
              ? { ...account, allocations: allocations, isExcluded: false }
              : account
          )
        );

        enqueueSnackbar('Account allocation rule created successfully', { variant: 'success' });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create allocation rule';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });

        setAllocationsMap((prev) => {
          const newMap = { ...prev };
          delete newMap[accountId];
          return newMap;
        });
      } finally {
        setSaving(null);
      }
    },
    [organisationId, projectId, enqueueSnackbar]
  );

  const handleExcludeAccount = useCallback(
    async (accountId: string) => {
      if (!organisationId || !projectId) return;

      setSaving(accountId);
      try {
        await saveAccountAllocations(organisationId, projectId, accountId, [], true);

        setAllocationsMap((prev) => {
          const newMap = { ...prev };
          delete newMap[accountId];
          return newMap;
        });

        setAccounts((prev) =>
          prev.map((account) =>
            account.id === accountId ? { ...account, allocations: [], isExcluded: true } : account
          )
        );

        enqueueSnackbar('Account excluded from R&D allocation', { variant: 'info' });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to exclude account';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSaving(null);
      }
    },
    [organisationId, projectId, enqueueSnackbar]
  );

  const handleDeleteRule = useCallback(
    async (accountId: string) => {
      if (!organisationId || !projectId) return;

      setSaving(accountId);
      try {
        const result = await deleteAccountAllocations(organisationId, projectId, accountId);

        setAllocationsMap((prev) => {
          const newMap = { ...prev };
          delete newMap[accountId];
          return newMap;
        });

        setAccounts((prev) =>
          prev.map((account) =>
            account.id === accountId ? { ...account, allocations: [], isExcluded: false } : account
          )
        );

        enqueueSnackbar(
          `Account allocation rule deleted (${result.deletedAllocationsCount} allocations removed)`,
          { variant: 'info' }
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to delete allocation rule';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSaving(null);
      }
    },
    [organisationId, projectId, enqueueSnackbar]
  );

  if (loading) {
    return (
      <ContentWrapper>
        <PaperSection
          title="Account Rules"
          action={
            <>
              <XeroSyncAccounts onSyncComplete={handleSyncComplete} />
              <AccountHelp />
            </>
          }
        >
          <LoadingComponent context="Loading accounts and activities..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  if (activities.length === 0) {
    return (
      <ContentWrapper>
        <PaperSection
          title="Account Rules"
          action={
            <>
              <XeroSyncAccounts onSyncComplete={handleSyncComplete} />
              <AccountHelp />
            </>
          }
        >
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, my: 10 }}
          >
            <EmptyStateContent
              icon={<ScienceOutlined />}
              title="No Research Activities"
              message="Create research activities to start setting up account allocation rules."
              button={
                <Button
                  component={RouterLink}
                  to={`/app/activities/${projectId}`}
                  variant="contained"
                  size="small"
                >
                  Create Your First Activity
                </Button>
              }
              showBorder={false}
            />
          </Box>
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title={`Account Rules - ${currentProject?.name}`}
        action={
          <>
            <XeroSyncAccounts onSyncComplete={handleSyncComplete} />
            <AccountHelp />
          </>
        }
      >
        <Box sx={{ mb: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {accounts.length > 0 && (
              <Box sx={{ mb: 2, mt: -2, minWidth: 400 }}>
                <TextField
                  fullWidth
                  size="small"
                  name="accountSearch"
                  id="accountSearch"
                  placeholder="Search by account code or name..."
                  value={searchFilter}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                {debouncedSearchFilter && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Showing {filteredAccounts.length} of {accounts.length} accounts
                  </Typography>
                )}
              </Box>
            )}

            {syncResult?.success && (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircle color="success" sx={{ fontSize: 16 }} />
                  <Typography variant="body2" color="success.main" fontWeight={500}>
                    Success!
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {syncResult.processed} processed
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    •
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {syncResult.created} created
                  </Typography>
                  {syncResult.updated > 0 && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {syncResult.updated} updated
                      </Typography>
                    </>
                  )}
                  {syncResult.skipped > 0 && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {syncResult.skipped} skipped
                      </Typography>
                    </>
                  )}
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>

        {filteredAccounts.length > 0 ? (
          <TableContainer
            sx={{
              mx: -6,
              px: 6,
              width: 'calc(100% + 96px)',
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                WebkitAppearance: 'none',
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                backgroundColor: 'rgba(0, 0, 0, .3)',
              },
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '450px', minWidth: '450px', fontWeight: 600 }}>
                    Accounts
                    {accounts.length > 0 && <span> ({accounts.length} total)</span>}
                  </TableCell>
                  <TableCell
                    sx={{ width: '120px', minWidth: '120px', textAlign: 'center', fontWeight: 600 }}
                  ></TableCell>
                  <TableCell
                    sx={{ width: '60px', minWidth: '60px', textAlign: 'center', fontWeight: 600 }}
                  ></TableCell>
                  {sortedActivities.map((activity) => (
                    <TableCell
                      key={activity.id}
                      sx={{ width: '100px', minWidth: '100px', p: 1, verticalAlign: 'top' }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={activity.type === ResearchActivityType.CORE ? 'CORE' : 'SUPP'}
                          size="small"
                          color={activity.type === ResearchActivityType.CORE ? 'info' : 'primary'}
                          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          align="center"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {activity.name}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.map((account) => {
                  return (
                    <AccountAllocationRow
                      key={account.id}
                      account={account}
                      sortedActivities={sortedActivities}
                      initialAllocations={allocationsMap[account.id] || {}}
                      saving={saving === account.id}
                      onCreateRule={(allocs) => handleCreateRule(account.id, allocs)}
                      onExcludeAccount={() => handleExcludeAccount(account.id)}
                      onDeleteRule={() => handleDeleteRule(account.id)}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : debouncedSearchFilter ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No accounts found matching "{debouncedSearchFilter}"
            </Typography>
            <Button size="small" onClick={clearFilter} sx={{ mt: 1 }}>
              Clear filter
            </Button>
          </Box>
        ) : (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<AutoFixHighOutlined />}
              title="No accounts found"
              message="No accounts found for this project. Sync with Xero to import accounts and create rules for R&D allocation."
              showBorder={false}
            />
          </Box>
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

export default Accounts;

const AccountHelp: React.FC = () => (
  <HelpComponent title="Create allocation rules for accounts.">
    <Typography variant="body2" paragraph>
      Each account can be allocated across activities creating a "rule" where transactions in each
      account will have the activity allocations pre-filled. Clicking "exclude" will exclude the
      account from automatic R&D allocation. Rules are specific to the current project.
    </Typography>
    <Divider sx={{ my: 1 }} />
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <DeleteSweep sx={{ fontSize: '16px', color: 'error.main' }} />
        <Typography variant="caption">Removes the selected rule</Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <PlaylistRemove sx={{ fontSize: '16px', color: 'primary.main' }} />
        <Typography variant="caption">Creates an "exclude" rule</Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <PlaylistAddCheck sx={{ fontSize: '16px', color: 'success.main' }} />
        <Typography variant="caption">Creates / saves an "allocation" rule</Typography>
      </Stack>
    </Stack>
    <Divider sx={{ my: 1 }} />
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AutoFixHigh sx={{ fontSize: '16px', color: 'success.main' }} />
        <Typography variant="caption">Shows rule state</Typography>
      </Stack>
    </Stack>
  </HelpComponent>
);
